import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Spinner } from 'components/Spinner'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { introducingBrokerProfileDetailsRouteUrl, links } from 'utils/featureToggles'

import { IntroducingBrokerProfileAccountGroupsPage } from './IntroducingBrokerProfileAccountGroupsPage'
import { IntroducingBrokerProfileDetailsPage } from './IntroducingBrokerProfileDetailsPage'
import { IntroducingBrokerProfileIbsPage } from './IntroducingBrokerProfileIbsPage'
import { IntroducingBrokerProfileInstrumentsPage } from './IntroducingBrokerProfileInstrumentsPage'
import { IntroducingBrokerProfileTemplate } from './IntroducingBrokerProfileTemplate'
import {
  getIntroducingBrokerProfileType,
  IntroducingBrokerProfileProps,
  IntroducingBrokerProfileTypes,
  MatchParams,
} from './types'

const IntroducingBrokerProfilePage: React.FC = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const params = useParams<MatchParams>()
  const [crumbs, setCrumbs] = useState([
    { title: 'Introducing Broker Profiles', link: links.brokerageIBProfiles.url },
  ])

  const [auth] = useContext(AuthSessionContext)
  const { callback: fetchPaymentAgents, forceRefresh } = useCallbackWithForceRefresh(
    () =>
      params.id
        ? partnershipsApiClient.getIntroducingBrokerProfileById(params.id)
        : Promise.resolve(null),
    [params.id]
  )
  const { data: profile, isLoading } = useFetchOne(fetchPaymentAgents)

  useEffect(() => {
    if (profile) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: profile.name,
          link: introducingBrokerProfileDetailsRouteUrl(
            profile,
            params.slug
              ? getIntroducingBrokerProfileType(params.slug)
              : IntroducingBrokerProfileTypes.DETAILS
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [profile]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <Spinner />
  }

  if (!profile) {
    return null
  }

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <IntroducingBrokerProfileTemplate profile={profile} permissions={auth?.permissions || []}>
        <IntroducingBrokerProfilePageFactory
          pageType={
            params.slug
              ? getIntroducingBrokerProfileType(params.slug)
              : IntroducingBrokerProfileTypes.DETAILS
          }
          profile={profile}
          permissions={auth?.permissions || []}
          forceRefresh={forceRefresh}
        />
      </IntroducingBrokerProfileTemplate>
    </React.Fragment>
  )
}

type IntroducingBrokerProfilePageFactoryProps = {
  pageType: IntroducingBrokerProfileTypes
} & IntroducingBrokerProfileProps

const IntroducingBrokerProfilePageFactory = (props: IntroducingBrokerProfilePageFactoryProps) => {
  switch (props.pageType) {
    case IntroducingBrokerProfileTypes.DETAILS:
      return (
        <MainTemplate>
          <IntroducingBrokerProfileDetailsPage {...props} />
        </MainTemplate>
      )
    case IntroducingBrokerProfileTypes.ACCOUNT_GROUP:
      return <IntroducingBrokerProfileAccountGroupsPage {...props} />
    case IntroducingBrokerProfileTypes.IBS:
      return <IntroducingBrokerProfileIbsPage {...props} />
    case IntroducingBrokerProfileTypes.INSTRUMENTS:
      return <IntroducingBrokerProfileInstrumentsPage {...props} />
    default:
      return <IntroducingBrokerProfileDetailsPage {...props} />
  }
}

export default IntroducingBrokerProfilePage
